/**
 * Pure logic for the TPA Learning Center data-sharing consent modal (COM-504).
 * No I/O, no React — the hook (`use-learning-center-consent`) passes in
 * `location.search`.
 *
 * The modal fires ONLY when the URL carries both an employer id and an employee
 * id. Key spellings mirror SFRA's SSO claim mapping, so a TPA link that SFRA
 * accepts also qualifies here. Keys are matched case-insensitively, which
 * covers `employerId` / `employerID` / `EmployerId` / `employerid` with the one
 * lowercase spelling below. `utm_`-prefixed keys are NOT accepted.
 *
 * No PII: the ids are only checked for presence — never read out, stored, or
 * logged.
 */

/**
 * Accepted URL param keys per id, lowercase. BOTH ids must be present with a
 * non-empty value for the consent modal to fire.
 */
export const EMPLOYER_CONSENT_URL_PARAMS = {
  employer: ["employer_id", "employerid", "employer-id"],
  employee: ["employee_id", "employeeid", "employee-id"],
} as const satisfies Record<string, readonly string[]>;

/**
 * True when the query string carries a non-empty employer id AND a non-empty
 * employee id under any {@link EMPLOYER_CONSENT_URL_PARAMS} key (keys matched
 * case-insensitively).
 */
export function urlHasEmployerParams(search: string): boolean {
  const present = new Set<string>();
  for (const [key, value] of new URLSearchParams(search)) {
    if (value) present.add(key.toLowerCase());
  }
  return Object.values(EMPLOYER_CONSENT_URL_PARAMS).every(keys =>
    keys.some(key => present.has(key)),
  );
}
